import mdAxios from 'core/common/config/axios-config';
import {parseId, addTenantToQuery} from 'core/common/utils/utils';
import {lovValue, IDomainObject} from "core/form/formUtils";
import {observable} from "mobx";
import {
  EventType, EventTemplate, EventHistory, ActivityConfig, ActivityTemplate,
  ACTIVITYLOCATION, TaskDto, Task, PartyActivity, EntityStatus, TaskNote, WithActive
} from "core/event/eventDomain";
import MdEntity from "core/metamodel/mdEntity";
import {ID} from "core/metamodel/metaConstant";
import {GridParams} from "core/grid/domain/grid";


class EventService  {


  @observable public eventTypeObject: EventType;
  @observable public eventTemplateObject: EventTemplate;
  @observable public activityConfigObject: ActivityConfig;
  @observable public activityTemplateObject: ActivityTemplate;

  @observable public eventHistories: EventHistory[];

  @observable public taskObject: Task;


  // ////////// History  ////////////////////////////////////////////////////////////////////////////////

  readHistory(entityName: string, entityId: ID) : Promise<EventHistory[]> {
    return mdAxios.get('/core/event/history/' + entityName + '/' + entityId)
      .then(response =>  response.data.eventHistories.map(ehJson => new EventHistory(ehJson)));
  }

  readEventHistory(id: number): Promise<EventHistory> {
    return mdAxios.get('/core/event/history/' + id)
      .then(response => new EventHistory(response.data));
  }

  // ////////// Event Active Action  ////////////////////////////////////////////////////////////////////////////////

  openObject(mdEntity, id): Promise<WithActive> {
    return mdAxios.post('/core/event/open', {mdEntity: mdEntity.id, id}, {showPrettyError: true})
      .then(response =>response.data);
  }

  closeObject(mdEntity, id, forceClose: boolean = false): Promise<WithActive> {
    return mdAxios.post('/core/event/close', {mdEntity: mdEntity.id, id}, {showPrettyError: true, params: {forceClose}})
      .then(response =>response.data);
  }


  // ////////// Event Type ////////////////////////////////////////////////////////////////////////////////

  readEventType(eventTypeId: string): Promise<EventType> {
    return mdAxios.get('/core/event/event-type/' + eventTypeId)
      .then(response => new EventType(response.data));
  }



  saveEventType(eventType: EventType): Promise<IDomainObject> {
    return mdAxios.post('/core/event/event-type', eventType, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteEventType(eventTypeId: string): Promise<any> {
    return mdAxios.delete('/core/event/event-type/' + eventTypeId, {showPrettyError: true});
  }


  // ////////// Event Template ////////////////////////////////////////////////////////////////////////////////


  readEventTemplate(id: string): Promise<EventTemplate> {
    let strings = parseId(id);
    return mdAxios.get('/core/event/event-template/' + strings[0] + '/' + strings[1])
      .then(response => new EventTemplate(response.data));
  }

  saveEventTemplate(eventTemplate: EventTemplate): Promise<IDomainObject> {
    return mdAxios.post('/core/event/event-template', eventTemplate, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteEventTemplate(eventTypeId: string, templateIndex: number): Promise<any> {
    return mdAxios.delete('/core/event/event-template/' + eventTypeId + '/' + templateIndex, {showPrettyError: true});
  }


  test(eventTemplate: EventTemplate, mdEntity: MdEntity, id: ID, dryRun: boolean): Promise<EventHistory> {
    return mdAxios.post('/core/event/test', {eventTemplate, dryRun, testSpec: {mdEntity: lovValue(mdEntity), id}}, {showPrettyError: true})
      .then((response) => new EventHistory(response.data));
  }


  // ////////// Activity Config ////////////////////////////////////////////////////////////////////////////////

  readActivityConfig(activityConfigId: string): Promise<ActivityConfig> {
    return mdAxios.get('/core/event/activity-config/' + activityConfigId)
      .then(response => new ActivityConfig(response.data));
  }



  saveActivityConfig(activityConfig: ActivityConfig): Promise<IDomainObject> {
    return mdAxios.post('/core/event/activity-config', activityConfig, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteActivityConfig(activityConfigId: string): Promise<any> {
    return mdAxios.delete('/core/event/activity-config/' + activityConfigId, {showPrettyError: true});
  }


  // ////////// Activity Template ////////////////////////////////////////////////////////////////////////////////


  readActivityTemplate(id: string): Promise<ActivityTemplate> {
    let strings = parseId(id);
    let url = '/core/event/activity-template/' + strings[0] + '/' + strings[1];
    return mdAxios.get(url)
      .then(response => new ActivityTemplate(response.data));
  }

  readActivityTemplates(activityConfigId: string): Promise<ActivityTemplate[]> {
    let url = '/core/event/activity-template/' + activityConfigId;
    return mdAxios.get(url)
      .then(response => response.data.map(json => new ActivityTemplate(json)) );
  }


  saveActivityTemplate(activityTemplate: ActivityTemplate): Promise<IDomainObject> {
    return mdAxios.post('/core/event/activity-template', activityTemplate, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteActivityTemplate(activityConfigId: string, templateIndex: number): Promise<any> {
    return mdAxios.delete('/core/event/activity-template/' + activityConfigId + '/' + templateIndex, {showPrettyError: true});
  }


  // ////////// Party Activity ////////////////////////////////////////////////////////////////////////////////


  readPartyActivity(id: number): Promise<PartyActivity> {
    return mdAxios.get('/core/event/activity/' + id)
      .then(response => new PartyActivity(response.data));
  }

  deletePartyActivity(gridParams: GridParams): Promise<any> {
    return mdAxios.post("/core/event/activity-delete", gridParams)
  }


  // ////////// Task ////////////////////////////////////////////////////////////////////////////////

  prepareTaskActivity(location: ACTIVITYLOCATION, gridParams: GridParams) : Promise<EntityStatus[]> {
    return mdAxios.post('/core/event/task-activity/' + location, gridParams )
      .then(response => response.data.map(item => new EntityStatus(item)));
  }

  createTask(taskDto: TaskDto): Promise<any> {
    return mdAxios.put('/core/event/task', taskDto, {showPrettyError: true})
      .then((response) => response.data);
  }

  updateTask(task: Task): Promise<IDomainObject> {
    return mdAxios.post('/core/event/task', task, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteTask(taskId: number): Promise<any> {
    return mdAxios.delete('/core/event/task/' + taskId, {showPrettyError: true})
      .then((response) => response.data);
  }

  readTask(id: string): Promise<Task> {
    return mdAxios.get('/core/event/task/' + id)
      .then(response => new Task(response.data));
  }

  createTaskNote(taskNote: TaskNote): Promise<IDomainObject> {
    return mdAxios.put('/core/event/task-note', taskNote, {showPrettyError: true})
      .then((response) => response.data);
  }

  closeTask(taskNote: TaskNote): Promise<IDomainObject> {
    return mdAxios.post('/core/event/task-close', taskNote, {showPrettyError: true})
      .then((response) => response.data);
  }

  reopenTask(taskId: number): Promise<IDomainObject> {
    return mdAxios.post('/core/event/task-reopen/' + taskId, null, {showPrettyError: true})
      .then((response) => response.data);
  }

  assignTask(taskId: number, username: string) : Promise<IDomainObject> {
    return mdAxios.post("/core/event/task-assign/" + taskId + "/" + username, null, {showPrettyError: true})
      .then( response => response.data);
  }
}

const eventService = new EventService();
export default eventService;




// WEBPACK FOOTER //
// ./src/core/event/eventService.tsx